import { Injectable } from '@angular/core';
import { ConstantsService } from './constants.service';
import { UtilsService } from './utils.service';
import { Router } from '@angular/router';
import { Logger } from '@app/core';
import { CookieService } from 'ng2-cookies';

/**
 * Declare and initialise a new Logger object instance.
 */
const log = new Logger('StateService');

/**
 * The State Service instance.
 */
@Injectable()
export class StateService {
  userProfile: any;
  private clientId: string;
  private isTpp: boolean;
  private tppScope: string;
  private authorizeUrl: string;
  private redirectUri: string;
  private returnState: string;
  private accessToken: string;
  private csrf: string;
  private referrer: string;
  private userName: string;

  /**
   * The constructor of the State Service.
   *
   * @param router The router service injected instance.
   * @param cookieService  The cookie service injected instance.
   */
  constructor(private router: Router, private cookieService: CookieService) {
    this.userProfile = {};
  }

  /**
   * Get username method.
   *
   * @returns string
   */
  getUserName(): string {
    return this.userName;
  }

  /**
   * Set user name method.
   *
   * @param value The value identifier.
   * @param autoSave The auto save state.
   */
  setUserName(value: string, autoSave = true): void {
    this.userName = value;
    this.saveState(autoSave);
  }

  /**
   * Get client id method.
   *
   * @returns string.
   */
  getClientId(): string {
    return this.clientId;
  }

  /**
   * Set client id method.
   *
   * @param value The value identifier.
   * @param autoSave The auto save state.
   */
  setClientId(value: string, autoSave = true): void {
    this.clientId = value;
    this.saveState(autoSave);
  }

  /**
   * Get client id tpp method.
   *
   * @returns boolean
   */
  getClientIsTpp(): boolean {
    return this.isTpp;
  }

  /**
   * Set client is tpp method.
   *
   * @param value The value identifier.
   * @param autoSave The auto save state.
   */
  setClientIsTpp(value: any, autoSave = true): void {
    this.isTpp = value;
    this.saveState(autoSave);
  }

  /**
   * Get tpp scope method.
   *
   * @returns String.
   */
  getTppScope(): string {
    return this.tppScope;
  }

  /**
   * Set tpp scope method.
   *
   * @param value The value identifier.
   * @param autoSave The auto save state.
   */
  setTppScope(value: string, autoSave = true): void {
    this.tppScope = value;
    this.saveState(autoSave);
  }

  /**
   * Get authorize url method.
   *
   * @returns String
   */
  getAuthorizeUrl(): string {
    return this.authorizeUrl;
  }

  /**
   * Set authorize url method.
   *
   * @param value The value identifier.
   * @param autoSave The auto save state.
   */
  setAuthorizeUrl(value: string, autoSave = true): void {
    this.authorizeUrl = value;
    this.saveState(autoSave);
  }

  /**
   * Get redirect uri method.
   *
   * @returns String
   */
  getRedirectUri(): string {
    return this.redirectUri;
  }

  /**
   * Set redirect uri method.
   *
   * @param value The value identifier.
   * @param autoSave The auto save state.
   */
  setRedirectUri(value: string, autoSave = true) {
    this.redirectUri = value;
    this.saveState(autoSave);
  }

  /**
   * Get return state method.
   *
   * @returns string.
   */
  getReturnState(): string {
    return this.returnState;
  }

  /**
   * Set return state mmethod.
   *
   * @param value The value identifier.
   * @param autoSave The auto save state.
   */
  setReturnState(value: string, autoSave = true) {
    this.returnState = value;
    this.saveState(autoSave);
  }

  /**
   * Get access token method.
   *
   * @returns string
   */
  getAccessToken(): string {
    return this.accessToken;
  }

  /**
   * Set access token method.
   *
   * @param value The value identifier.
   * @param autoSave The auto save state.
   */
  setAccessToken(value: string, autoSave = true) {
    this.accessToken = value;
    this.saveState(autoSave);
  }

  /**
   * Get referrer method.
   *
   * @returns String
   */
  getReferrer(): string {
    return this.referrer;
  }

  /**
   * Set referrer method.
   *
   * @param value The value identifier.
   * @param autoSave The auto save state.
   */
  setReferrer(value: string, autoSave = true) {
    this.referrer = value;
    this.saveState(autoSave);
  }

  /**
   * get csrf method.
   *
   * @returns string
   */
  getCsrf(): string {
    return this.csrf;
  }

  /**
   * Set csrf method.
   *
   * @param value The value identifier.
   * @param autoSave The auto save state.
   */
  setCsrf(value: string, autoSave = true) {
    this.csrf = value;
    this.saveState(autoSave);
  }

  /**
   * Get state object method.
   *
   * @returns
   */
  getStateObject() {
    const result = {}; // tslint:disable-line
    if (this.getClientId()) {
      const clientIdString = 'client_id';
      result[clientIdString] = this.getClientId();
    }
    if (this.getClientIsTpp()) {
      const isTppString = 'is_tpp';
      result[isTppString] = this.getClientIsTpp();
    }
    if (this.getTppScope()) {
      const scopeString = 'scope';
      result[scopeString] = this.getTppScope();
    }
    if (this.getAuthorizeUrl()) {
      const authorizeUrl = 'authorize_url';
      result[authorizeUrl] = this.getAuthorizeUrl();
    }
    if (this.getRedirectUri()) {
      const redirectUriString = 'redirectUri';
      result[redirectUriString] = this.getRedirectUri();
    }
    if (this.getReturnState()) {
      const returnStateString = 'returnState';
      result[returnStateString] = this.getReturnState();
    }
    if (this.getAccessToken()) {
      const accessTokenString = 'accessToken';
      result[accessTokenString] = this.getAccessToken();
    }
    if (this.getUserName()) {
      const userNameString = 'userName';
      result[userNameString] = this.getUserName();
    }
    if (this.getCsrf()) {
      const csrfString = 'csrf';
      result[csrfString] = this.getCsrf();
    }
    return result;
  }

  /**
   * Set state object method.
   *
   * @param state The state identifier.
   */
  setStateObject(state: any): void {
    if (state.client_id) {
      this.setClientId(state.client_id, false);
    }
    if (state.is_tpp) {
      this.setClientIsTpp(state.is_tpp, false);
    }
    if (state.scope) {
      this.setTppScope(state.scope, false);
    }
    if (state.authorize_url) {
      this.setAuthorizeUrl(state.authorize_url, false);
    }
    if (state.srcClientId) {
      this.setClientId(state.srcClientId, false);
    }
    if (state.redirectUri) {
      this.setRedirectUri(state.redirectUri, false);
    }
    if (state.returnState) {
      this.setReturnState(state.returnState, false);
    }
    if (state.accessToken) {
      this.setAccessToken(state.accessToken, false);
    }
    if (state.userName) {
      this.setUserName(state.userName, false);
    }
    if (state.csrf) {
      this.setCsrf(state.csrf, false);
    }
    this.saveState();
  }

  /**
   * Get state name method.
   *
   * @returns
   */
  getStateName() {
    return this.getReferrer() ? 'state_' + this.getReferrer() : 'state';
  }

  /**
   * The save state method.
   *
   * @param autoSave The auto save state
   */
  saveState(autoSave = true): void {
    if (autoSave) {
      log.debug('saveState', this.getStateObject());
      this.cookieService.set(
        'state',
        UtilsService.encodeBase64(this.getStateObject()),
        null,
        ConstantsService.cookiePath,
        window.location.hostname === 'localhost' ? '' : 'bcr.ro'
      );
      this.cookieService.set(
        this.getStateName(),
        UtilsService.encodeBase64(this.getStateObject()),
        null,
        ConstantsService.cookiePath,
        window.location.hostname === 'localhost' ? '' : 'bcr.ro'
      );
    }
  }

  /**
   * The retrieve state method.
   */
  retrieveState(): void {
    if (this.cookieService.check(this.getStateName())) {
      if (UtilsService.decodeBase64(this.cookieService.get(this.getStateName())).hasOwnProperty('is_tpp')) {
        this.deleteTppAndSetState();
      } else {
        log.debug('retrieveState', UtilsService.decodeBase64(this.cookieService.get(this.getStateName())));
        this.setStateObject(UtilsService.decodeBase64(this.cookieService.get(this.getStateName())));
      }
    }

    const state = UtilsService.getParameterByName('state');
    if (state) {
      this.setStateObject(UtilsService.decodeBase64(state));
    }
    const clientId = UtilsService.getParameterByName('client_id');
    if (clientId) {
      this.setClientId(clientId);
    }
    const clientIsTpp = UtilsService.getParameterByName('is_tpp');
    if (clientIsTpp) {
      log.info('client_is_tpp');
      this.setClientIsTpp(clientIsTpp, true);
    }
    const tppScopeFromState = UtilsService.getParameterByName('scope');
    if (tppScopeFromState) {
      this.setTppScope(tppScopeFromState, true);
    }
    const authorizeUrlTppFromState = UtilsService.getParameterByName('authorize_url');
    if (authorizeUrlTppFromState) {
      this.setAuthorizeUrl(authorizeUrlTppFromState, true);
    }
    const sectionParam = UtilsService.getParameterByName('section');
    if (sectionParam) {
      this.setReturnState(sectionParam);
    }
    const csrfParam = UtilsService.getParameterByName('csrf_token');
    if (csrfParam) {
      this.setCsrf(csrfParam);
    }
    try {
      const hash = UtilsService.decodeBase64(window.location.hash.substr(1));
      log.debug('hash', hash.client_id);
      if (hash.hasOwnProperty('is_tpp')) {
        this.setClientIsTpp(hash.is_tpp, true);
        log.debug('is_tpp', hash.is_tpp);
      }
      if (hash.hasOwnProperty('scope')) {
        this.setTppScope(hash.scope, true);
        log.debug('scope', hash.scope);
      }
      if (hash.hasOwnProperty('authorize_url')) {
        this.setAuthorizeUrl(hash.authorize_url, true);
        log.debug('authorize_url', hash.authorize_url);
      }
      if (hash.hasOwnProperty('client_id')) {
        this.setClientId(hash.client_id);
        log.debug('client_id', hash.client_id);
        window.location.hash = '';
      }
    } catch (e) {}
  }

  /**
   * The validate client id method.
   */
  validateClientId(): void {
    if (this.getClientId() === undefined) {
      this.router.navigate(['errorPage']);
    }
  }

  /**
   * The delete tpp and set state method.
   */
  deleteTppAndSetState(): void {
    const localState = UtilsService.decodeBase64(this.cookieService.get(this.getStateName()));
    const isTppString = 'is_tpp';
    delete localState[isTppString];
    this.setStateObject(localState);
  }
}
